// The `/benchmark` route's element: the instrument, fully composed.
//
// This is the ONLY place the two phases' real implementations are named. The shell mounts this
// as an opaque element and learns nothing about it — no seam in `AppRoutesProps`, no session, no
// credential, no store slice — so neither mode's screen count moves and removing the instrument
// later would require no change to any other route.
//
// The render phase needs a live DOM container to build a `CanvasRenderer` over, which is the one
// thing the screen itself cannot supply (it is presentation, and a container is a host object).
// The container is mounted here, beside the screen, and handed to the phase when it runs.

import { useCallback, useRef } from 'react';

import { BenchmarkScreen } from './BenchmarkScreen';
import { runBenchmarkRenderPhase } from './benchmark-render-phase';
import { runBenchmarkSimPhase } from './benchmark-sim-phase';
import { phaseFailed, type RenderPhaseResult, type SimPhaseResult } from './report';

/**
 * The snapshot cadence the synthetic render scene is presented at.
 *
 * **This is not a read of the balance mirror**, and it is deliberately not the benchmark
 * preset's 5 Hz either. There is no run here — the scene is synthetic and every frame carries a
 * fresh snapshot — so this value only sets the interpolation window the renderer smooths over.
 * It is pinned at the shipped play rate because the quantity being measured is what a player
 * feels while watching a close-tier field, and interpolating over a 200 ms window instead of a
 * 67 ms one would smooth a different amount of motion and measure a different renderer.
 */
export const BENCHMARK_RENDER_TICK_RATE_HZ = 15;

export function BenchmarkRoute() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const runSimulation = useCallback((): Promise<SimPhaseResult> => runBenchmarkSimPhase(), []);

  const runRender = useCallback(async (): Promise<RenderPhaseResult> => {
    const container = containerRef.current;
    if (container === null) {
      // Recorded as a failure rather than thrown: the screen has to be able to SHOW it, and the
      // submitted report has to carry it. An absent phase and a failed phase are different facts.
      return phaseFailed('render_container_unavailable');
    }
    return runBenchmarkRenderPhase(container, BENCHMARK_RENDER_TICK_RATE_HZ);
  }, []);

  return (
    <>
      <BenchmarkScreen runSimulation={runSimulation} runRender={runRender} />
      {/* The renderer's host. Present from mount so the phase never races the ref, and empty
          until the phase builds a `CanvasRenderer` into it. */}
      <div ref={containerRef} data-testid="benchmark-render-container" />
    </>
  );
}
